import { defineStore } from 'pinia'
import { ref } from 'vue'

import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'

import type { ProjectPermissionType, Role } from '@/stores/type/authorization.type'
import { useBaseStore } from '@haskoning/vue-basestore'

const { api } = useApi()

const baseUrl = API.ROLES.INDEX

export const useAuthorizationStore = defineStore('authorization', () => {
  const baseStore = useBaseStore<Role>({ service: baseUrl, api })

  const currentRoles = ref<Role[]>([])

  function setCurrentRole(roleIds: number[]) {
    currentRoles.value = roleIds
      .map((roleId: number) => getCurrentRole(roleId))
      .filter((role): role is Role => Boolean(role))
  }

  function getCurrentRole(roleId: number) {
    const roles = Array.isArray(baseStore.items.value) ? baseStore.items.value : []
    return roles.find((item) => item.id === roleId)
  }

  function hasServicePermission({
    authSource,
    allowed
  }: {
    authSource: string
    allowed: string[]
  }) {
    const authRoles = currentRoles.value.find((role) => role.auth_source === authSource)
    return authRoles
      ? authRoles.permissions.some((permission) => allowed.includes(permission))
      : false
  }

  function resetState() {
    baseStore.reset()
    currentRoles.value = []
  }

  function getAuthPermissionItems(roleItem: { id: number; role: number }, permissions: string[]) {
    const authPermission: string[] = []
    permissions.forEach((permissionItem) => {
      const authRoles = getCurrentRole(roleItem.role)
      if (!authRoles) return
      const hasPermission = authRoles.permissions.includes(permissionItem)
      if (hasPermission) {
        const p = permissionItem.split('_')[0]
        authPermission.push(p)
      }
    })
    return authPermission
  }

  async function getServicePermissionByRole(
    roleItems: { id: number; role: number }[],
    permissions: string[]
  ): Promise<ProjectPermissionType[]> {
    if (!Array.isArray(baseStore.items.value) || !baseStore.items.value.length) {
      await baseStore.fetchItems()
    }
    const authPermission: ProjectPermissionType[] = []
    roleItems.forEach((roleItem) => {
      const authPermissionItems = getAuthPermissionItems(roleItem, permissions)
      authPermission.push({
        ...roleItem,
        permissions: authPermissionItems
      })
    })
    return authPermission
  }

  return {
    ...baseStore,
    currentRoles,
    setCurrentRole,
    getCurrentRole,
    hasServicePermission,
    resetState,
    getServicePermissionByRole
  }
})
